import { useGSAP } from '@gsap/react'
import React, { useRef, useState, useEffect } from 'react'
import { animateWithGsap } from '../utils/animations';
import { explore1Img, explore2Img, exploreVideo } from '../utils';
import gsap from 'gsap';
import api, { SERVER_URL } from '@/lib/api';
import { motion } from 'framer-motion';

const Features = () => {
  const videoRef = useRef();
  const [content, setContent] = useState({
    title: 'Explore the full story.',
    items: [] // Dynamic bento items could be rendered here if we refactor the hardcoded layout
  });

  useEffect(() => {
    // Hardcoded placeholder items
    const placeholderItems = [
      {
        id: 1,
        title: "Curriculum Relevant",
        description: "Meticulously designed modules that align with academic and professional standards.",
        image: "/bento/2.jpg",
        overlayText: "CURRICULUM RELEVANT",
        colSpan: 2
      },
      {
        id: 2,
        title: "Global Accessibility",
        description: "Accessible anywhere in the world, breaking borders in AI training.",
        image: "/bento/3.jpg",
        overlayText: "GLOBAL"
      },
      {
        id: 3,
        title: "Business Oriented",
        description: "Focused on incorporating AI into corporate environments and business processes.",
        image: "/bento/4.jpg",
        overlayText: "BUSINESS AND CORPORATE ALIGNED"
      }
    ];
    setContent(prev => ({ ...prev, items: placeholderItems }));
  }, []);

  useGSAP(() => {
    gsap.to('#exploreVideo', {
      scrollTrigger: {
        trigger: '#exploreVideo',
        toggleActions: 'play pause reverse restart',
        start: '-10% bottom',
      },
      onComplete: () => {
        videoRef.current.play();
      }
    })

    animateWithGsap('#features_title', { y: 0, opacity: 1 })
    animateWithGsap(
      '.g_grow',
      { scale: 1, opacity: 1, ease: 'power1' },
      { scrub: 5.5 }
    );
    animateWithGsap(
      '.g_text',
      { y: 0, opacity: 1, ease: 'power2.inOut', duration: 1 }
    )
  }, []);

  return (
    <section id="features" className="h-full common-padding bg-white dark:bg-black relative overflow-hidden transition-colors duration-500">
      <div className="screen-max-width">
        <div className="mb-12 w-full">
          <h1 id="features_title" className="section-heading">{content.title}</h1>
        </div>

        <div className="flex flex-col justify-center items-center overflow-hidden">
          <div className="mt-32 mb-24 pl-24">
            <h2 className="text-5xl lg:text-7xl font-semibold text-black dark:text-white feature-title-shadow">SNC AI Training.</h2>
            <h2 className="text-5xl lg:text-7xl font-semibold text-black dark:text-white feature-title-shadow">Forged for the future.</h2>
          </div>

          <div className="flex-center flex-col sm:px-10">
            <div className="relative h-[60vh] w-full flex items-center rounded-3xl overflow-hidden">
               <img 
                src="/bento/1.jpg" 
                alt="AI Neural Architecture" 
                className="w-full h-full object-cover g_grow"
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.h2 
                  initial={{ opacity: 0, y: 50, scale: 0.8 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                  className="text-white text-6xl md:text-8xl font-black tracking-tighter opacity-20 select-none uppercase"
                >
                  Practical AI Mastery
                </motion.h2>
              </div>
            </div>

            <div className="flex flex-col w-full relative">
              {/* Dynamic Bento Grid Items */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
                {content.items.map((item, index) => (
                  <div
                    key={item.id || index}
                    className={`relative overflow-hidden rounded-3xl bg-zinc-900 h-[50vh] group ${item.colSpan === 2 ? 'md:col-span-2' : ''}`}
                  >
                    {item.image && (
                      <img
                        src={item.image.startsWith('http') || item.image.startsWith('/assets') || item.image.startsWith('/bento') ? item.image : `${SERVER_URL}${item.image.startsWith('/') ? '' : '/'}${item.image}`}
                        alt={item.title}
                        className="w-full h-full object-cover g_grow"
                      />
                    )}
                    
                    {/* Animated AI Text Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden p-4 text-center">
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.8 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                      >
                        <motion.div
                          animate={{ 
                            y: [0, -10, 0],
                            rotate: [-1, 1, -1]
                          }}
                          transition={{ 
                            duration: 4, 
                            repeat: Infinity, 
                            ease: "easeInOut" 
                          }}
                        >
                          <span className="text-white text-5xl md:text-7xl font-black tracking-tighter opacity-20 select-none uppercase">
                            {item.overlayText || "SNC AI"}
                          </span>
                        </motion.div>
                      </motion.div>
                    </div>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-8 flex flex-col justify-end">
                      <motion.h3 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-2xl font-bold text-white mb-2"
                      >
                        {item.title}
                      </motion.h3>
                      <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="text-gray-300"
                      >
                        {item.description}
                      </motion.p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Keep some of the original storytelling text but update it */}
              <div className="feature-text-container mt-20">
                <div className="flex-1 flex-center">
                  <p className="feature-text g_text">
                    SNC AI Training is {' '}
                    <span className="text-zinc dark:text-white">
                      the premier ecosystem for mastering autonomous agents
                    </span>,
                    using advanced AI models designed for real-world impact.
                  </p>
                </div>

                <div className="flex-1 flex-center">
                  <p className="feature-text g_text">
                    Our modular approach offers one of the best learning-to-implementation ratios, making these our {' '}
                    <span className="text-zinc dark:text-white">
                      most advanced training modules ever.
                    </span>
                    You'll notice the difference the moment you start building.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Features